/**
 * Utility functions for generating unique IDs
 */

/**
 * Spanish license-plate consonants (20 letters).
 * Excludes vowels (A E I O U) and Q (ambiguous), matching the real Spanish DGT system.
 */
const PLATE_LETTERS = 'BCDFGHJKLMNPRSTVWXYZ'; // 20 letters
const L = PLATE_LETTERS.length; // 20

/**
 * Converts a sequential integer (1, 2, 3 ...) into the Spanish license-plate
 * format:  NNNN·LLL  (4 zero-padded digits + 3 consonants).
 *
 * Sequence:
 *   1 → 0001·BBB
 *   2 → 0002·BBB
 *   9999 → 9999·BBB
 *   10000 → 0001·BBC
 *   …
 *   Total capacity ≈ 80 million parts per company.
 *
 * Returns '----·---' for invalid/null input.
 */
export function formatPartNumber(n: number | null | undefined): string {
  if (!n || n < 1) return '----\u00B7---';
  const CYCLE = 9999 * L * L * L; // ~79,992,000 per cycle
  const cycle = Math.floor((n - 1) / CYCLE); // 0 = first cycle, 1 = second, etc.
  const pos   = ((n - 1) % CYCLE);           // position within the current cycle
  const digits    = (pos % 9999) + 1;
  const letterIdx = Math.floor(pos / 9999);
  const l3 = letterIdx % L;
  const l2 = Math.floor(letterIdx / L) % L;
  const l1 = Math.floor(letterIdx / (L * L)) % L;
  const numStr = digits.toString().padStart(4, '0');
  const letStr = PLATE_LETTERS[l1] + PLATE_LETTERS[l2] + PLATE_LETTERS[l3];
  const prefix = cycle > 0 ? `[${cycle + 1}]` : '';
  return `${prefix}${numStr}\u00B7${letStr}`; // e.g. "0001·BBB" or "[2]0001·BBB"
}

/**
 * Generates a unique ID using timestamp and random string
 * Format: prefix-timestamp-randomString
 * Example: WO-1702684800000-x7k9m2p
 */
export const generateUniqueId = (prefix: string = 'ID'): string => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 11);
    return `${prefix}-${timestamp}-${randomStr}`;
};

/**
 * Generates a work order ID
 * Format: WO-YYYY-timestamp-random
 * Example: WO-2025-1702684800000-x7k9m2p
 */
export const generateWorkOrderId = (): string => {
    const year = new Date().getFullYear();
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 11);
    return `WO-${year}-${timestamp}-${randomStr}`;
};

/**
 * Generates a valid UUID v4
 */
export const generateUUID = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

/**
 * Generates a client ID (UUID)
 */
export const generateClientId = (): string => {
    return generateUUID();
};

/**
 * Generates a technician ID (UUID)
 */
export const generateTechnicianId = (): string => {
    return generateUUID();
};

/**
 * Generates a work site ID (UUID)
 */
export const generateWorkSiteId = (): string => {
    return generateUUID();
};

/**
 * Generates an event ID (for incidents and material requests)
 * Local id, used by frontend specifically with 'ev-' prefix to determine if they're new
 */
export const generateEventId = (): string => {
    return `ev-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Validates if an ID has the expected format
 */
export const validateIdFormat = (id: string, prefix: string): boolean => {
    const pattern = new RegExp(`^${prefix}-(?:\\d{4}-)?\\d+-[a-z0-9]+$`);
    return pattern.test(id);
};
